export type PwaUpdateState =
  | { status: "unsupported" }
  | { status: "current"; registration: ServiceWorkerRegistration }
  | { status: "update-available"; registration: ServiceWorkerRegistration }
  | { status: "offline-ready"; registration: ServiceWorkerRegistration }
  | { status: "error"; error: Error };

export class PwaUpdateManager {
  private registration?: ServiceWorkerRegistration;
  private readonly listeners = new Set<(state: PwaUpdateState) => void>();

  subscribe(listener: (state: PwaUpdateState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async register(): Promise<ServiceWorkerRegistration | undefined> {
    if (!("serviceWorker" in navigator)) {
      this.emit({ status: "unsupported" });
      return undefined;
    }
    try {
      const registration = await navigator.serviceWorker.register("/service-worker.js", { type: "module" });
      this.registration = registration;
      if (registration.waiting && navigator.serviceWorker.controller) {
        this.emit({ status: "update-available", registration });
      } else {
        this.emit({
          status: navigator.serviceWorker.controller ? "current" : "offline-ready",
          registration,
        });
      }
      registration.addEventListener("updatefound", () => this.watchInstalling(registration));
      return registration;
    } catch (error) {
      this.emit({ status: "error", error: toError(error) });
      return undefined;
    }
  }

  async check(): Promise<void> {
    await this.registration?.update();
  }

  /** Activates a waiting build only after the UI obtains user confirmation. */
  async activateUpdate(reload = true): Promise<void> {
    const waiting = this.registration?.waiting;
    if (!waiting) return;
    const controlled = new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true });
    });
    waiting.postMessage({ type: "SKIP_WAITING" });
    await controlled;
    if (reload) window.location.reload();
  }

  private watchInstalling(registration: ServiceWorkerRegistration): void {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      if (installing.state !== "installed") return;
      this.emit({
        status: navigator.serviceWorker.controller ? "update-available" : "offline-ready",
        registration,
      });
    });
  }

  private emit(state: PwaUpdateState): void {
    this.listeners.forEach((listener) => listener(state));
  }
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

